import { ActiveOffer } from '@/types/offer';
import { API_BASE_URL } from '@/config/api';

export interface Product {
  id: string;
  name: string;
  price: number;
  appPrice: number;
  originalPrice: number;
  effectivePrice?: number;
  offerPrice?: number;
  offerDiscountAmount?: number;
  hasActiveOffer?: boolean;
  activeOffer?: ActiveOffer;
  image: string;
  category?: string;
  categorySlug?: string;
  productSlug?: string;
  badge?: string;
  rating?: number;
  description?: string;
  benefits?: string[];
  urls?: string[];
  videoUrls?: string[];
  isActive?: boolean;
  isAvailable?: boolean;
  suitableFor?: string;
  howToWear?: string;
  bestDayToWear?: string;
  disclaimer?: string;
  packaging?: string;
  returnPolicy?: string;
  gemstoneVariants?: GemstoneVariant[];
  relatedProducts?: Product[];
}

export interface GemstoneVariant {
  country: string;
  grade: string;
  carat: number;
  price: number;
  originalPrice?: number;
}

export const getNormalizedProductPrice = (product: Partial<Product> | Record<string, unknown>) => {
  const p = product as Partial<Product>;
  const basePrice = p.appPrice ?? p.price ?? 0;
  const finalPrice =
    p.hasActiveOffer === true && p.effectivePrice != null
      ? p.effectivePrice
      : (p.hasActiveOffer === true && p.offerPrice != null ? p.offerPrice : basePrice);
  const discountAmount = p.offerDiscountAmount ?? (p.hasActiveOffer ? Math.max(0, basePrice - finalPrice) : 0);

  return {
    basePrice,
    finalPrice,
    originalPrice: p.originalPrice ?? basePrice,
    hasActiveOffer: p.hasActiveOffer === true,
    activeOffer: p.activeOffer,
    discountAmount,
  };
};

const normalizeImageUrl = (url: string | undefined): string => {
  if (!url || typeof url !== "string") return "/placeholder.svg";
  const trimmed = url.trim();
  if (!trimmed) return "/placeholder.svg";
  try {
    const decoded = decodeURI(trimmed);
    return encodeURI(decoded);
  } catch {
    return encodeURI(trimmed);
  }
};

export const mapProductItem = (item: Record<string, unknown>): Product => {
  const appPriceNum = typeof item.appPrice === 'number' ? item.appPrice : undefined;
  const priceNum = typeof item.price === 'number' ? item.price : 0;
  const basePrice = appPriceNum ?? priceNum;
  const effectivePriceNum = typeof item.effectivePrice === 'number' ? item.effectivePrice : undefined;
  const offerPriceNum = typeof item.offerPrice === 'number' ? item.offerPrice : undefined;
  const hasActiveOffer = item.hasActiveOffer === true;

  const finalPrice =
    hasActiveOffer && effectivePriceNum != null
      ? effectivePriceNum
      : (hasActiveOffer && offerPriceNum != null ? offerPriceNum : basePrice);

  const rawVariants = Array.isArray(item.gemstoneVariants) ? item.gemstoneVariants : [];
  const gemstoneVariants: GemstoneVariant[] = rawVariants.map((v: Record<string, unknown>) => ({
    country: String(v.country || ''),
    grade: String(v.grade || ''),
    carat: Number(v.carat) || 0,
    price: Number(v.price) || 0,
    originalPrice: v.originalPrice != null ? Number(v.originalPrice) : undefined,
  }));

  const product: Product = {
    id: String(item.productId || ''),
    name: String(item.productName || ''),
    price: finalPrice,
    appPrice: basePrice,
    originalPrice: typeof item.originalPrice === 'number' ? item.originalPrice : basePrice,
    effectivePrice: effectivePriceNum,
    offerPrice: offerPriceNum,
    offerDiscountAmount: typeof item.offerDiscountAmount === 'number' ? item.offerDiscountAmount : undefined,
    hasActiveOffer,
    activeOffer: (item.activeOffer as ActiveOffer) || undefined,
    image: normalizeImageUrl(typeof item.imageUrl === 'string' ? item.imageUrl : undefined),
    category: typeof item.category === 'string' ? item.category : undefined,
    categorySlug: typeof item.categorySlug === 'string' ? item.categorySlug : undefined,
    productSlug: typeof item.productSlug === 'string' ? item.productSlug : undefined,
    badge: item.category === "Bracelets" ? undefined : (typeof item.category === 'string' ? item.category : undefined),
    rating: typeof item.reviews === 'number' ? item.reviews : undefined,
    description: typeof item.description === 'string' ? item.description : undefined,
    benefits: Array.isArray(item.benefits) ? (item.benefits as string[]) : undefined,
    urls: Array.isArray(item.urls) ? (item.urls as string[]) : [],
    videoUrls: Array.isArray(item.videoUrls) ? (item.videoUrls as string[]) : [],
    isActive: item.isActive !== undefined ? Boolean(item.isActive) : true,
    isAvailable: item.isAvailable !== false,
    suitableFor: typeof item.suitableFor === 'string' ? item.suitableFor : '',
    howToWear: typeof item.howToWear === 'string' ? item.howToWear : '',
    bestDayToWear: typeof item.bestDayToWear === 'string' ? item.bestDayToWear : '',
    disclaimer: typeof item.disclaimer === 'string' ? item.disclaimer : '',
    packaging: typeof item.packaging === 'string' ? item.packaging : '',
    returnPolicy: typeof item.returnPolicy === 'string' ? item.returnPolicy : '',
    gemstoneVariants,
  };
  product.relatedProducts = Array.isArray(item.relatedProducts)
    ? item.relatedProducts.map((related) => mapProductItem(related as Record<string, unknown>))
    : [];
  return product;
};

const fetchOneProduct = async (path: string): Promise<Product> => {
  const response = await fetch(`${API_BASE_URL}${path}`);
  if (!response.ok) {
    if (response.status === 404) throw new Error("Product not found");
    throw new Error("Failed to fetch product");
  }
  return mapProductItem(await response.json());
};

export const fetchProductBySlugs = (categorySlug: string, productSlug: string): Promise<Product> =>
  fetchOneProduct(
    `/read/v1/service/product/${encodeURIComponent(categorySlug)}/${encodeURIComponent(productSlug)}`
  );

export const fetchProductById = (productId: string): Promise<Product> =>
  fetchOneProduct(`/read/v1/service/product/id/${encodeURIComponent(productId)}`);

export const fetchProducts = async (searchText: string = "", category: string = ""): Promise<Product[]> => {
  const params = new URLSearchParams();
  if (searchText) params.append('text', searchText);
  if (category && category !== 'all') params.append('category', category);

  const response = await fetch(
    `${API_BASE_URL}/read/v1/service/products?${params.toString()}`
  );
  
  if (!response.ok) {
    throw new Error("Failed to fetch products");
  }
  
  const data = await response.json();
  const list = Array.isArray(data?.list) ? data.list : [];
  return list.map((item: Record<string, unknown>) => mapProductItem(item));
};

export const fetchAdminProducts = async (
  token: string,
  searchText: string = "",
  category: string = ""
): Promise<Product[]> => {
  const params = new URLSearchParams();
  if (searchText) params.append('text', searchText);
  if (category && category !== 'all') params.append('category', category);

  const response = await fetch(
    `${API_BASE_URL}/api/product/fetch?${params.toString()}`,
    {
      headers: { token },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch admin products");
  }

  const data = await response.json();
  const list = Array.isArray(data?.list) ? data.list : [];
  return list.map((item: Record<string, unknown>) => mapProductItem(item));
};
